/**
 * useSharedWithMe — hooks and helpers for surfacing shared-with-me notes
 * (shared_note_refs pointer + shared_notes content cache) inside the main
 * note list and command palette.
 *
 * Shared notes never live in the `notes` table. These hooks synthesize
 * read-only LocalNote stubs (flagged `sharedWithMe: true`) so the list,
 * virtualizer, and navigation can treat them uniformly, while the UI gates
 * them to a limited action set and the shared-note viewer.
 */

import { parseSearchQuery } from '@caliu/utils'
import { useFingerprintedQuery } from '../lib/reactive'
import type { LocalNote } from '../lib/db'
import { query, queryOne } from '../lib/sqlite'
import { searchSharedNoteIds } from '../lib/searchSql'
import { useAuthStore } from '../store/auth'

interface SharedStubRow {
  id: string
  title: string | null
  content: string | null
  author_name: string | null
  created_at: string | null
  updated_at: string | null
  added_at: string
}

function rowToSharedStub(row: SharedStubRow): LocalNote {
  return {
    id: row.id,
    userId: '',
    title: row.title ?? '',
    content: row.content ?? '',
    isPinned: false,
    isEncrypted: false,
    wordCount: 0,
    tags: [],
    createdAt: row.created_at ?? row.added_at,
    updatedAt: row.updated_at ?? row.added_at,
    syncStatus: 'synced',
    sharedWithMe: true,
    sharedAuthorName: row.author_name ?? '',
  }
}

const SHARED_STUB_SELECT = `
  SELECT snr.note_id AS id,
         sn.title AS title,
         sn.content AS content,
         sn.author_name AS author_name,
         sn.created_at AS created_at,
         sn.updated_at AS updated_at,
         snr.added_at AS added_at
  FROM shared_note_refs snr
  LEFT JOIN shared_notes sn ON sn.id = snr.note_id
  WHERE snr.user_id = ?`

/** True while `noteId` is in the user's shared-with-me list. */
export function useIsSharedWithMeNote(noteId: string | undefined): boolean {
  const userId = useAuthStore((s) => s.user?.id)
  return (
    useFingerprintedQuery<boolean>({
      tables: ['shared_note_refs'],
      fingerprint: () => {
        if (!noteId || !userId) return 'none'
        const row = queryOne<{ id: string }>(
          'SELECT id FROM shared_note_refs WHERE user_id = ? AND note_id = ?',
          [userId, noteId],
        )
        return row ? 'yes' : 'no'
      },
      query: () => {
        if (!noteId || !userId) return false
        return !!queryOne<{ id: string }>(
          'SELECT id FROM shared_note_refs WHERE user_id = ? AND note_id = ?',
          [userId, noteId],
        )
      },
      deps: [noteId, userId],
    }) ?? false
  )
}

/**
 * Read-only LocalNote stubs for every shared-with-me note, keyed by note id.
 * Merged over the `notes`-backed map in NoteList so shared rows render in
 * All Notes. One batch subscription for all stubs (never per-row).
 */
export function useSharedStubsMap(): Map<string, LocalNote> {
  const userId = useAuthStore((s) => s.user?.id)
  return (
    useFingerprintedQuery<Map<string, LocalNote>>({
      tables: ['shared_note_refs', 'shared_notes'],
      fingerprint: () => {
        if (!userId) return ''
        const rows = query<{ id: string; updated_at: string | null; added_at: string }>(
          `SELECT snr.note_id AS id, sn.updated_at, snr.added_at
           FROM shared_note_refs snr
           LEFT JOIN shared_notes sn ON sn.id = snr.note_id
           WHERE snr.user_id = ?`,
          [userId],
        )
        return rows.map((r) => `${r.id}:${r.updated_at ?? ''}:${r.added_at}`).join('|')
      },
      query: () => {
        if (!userId) return new Map<string, LocalNote>()
        const rows = query<SharedStubRow>(SHARED_STUB_SELECT, [userId])
        return new Map(rows.map((r) => [r.id, rowToSharedStub(r)]))
      },
      deps: [userId],
    }) ?? new Map<string, LocalNote>()
  )
}

/**
 * Shared-with-me notes the search query matches, for the command palette.
 * Pure function (no hooks) — mirrors searchNoteIdsByContent for own notes.
 */
export function searchSharedNotes(q: string, limit: number): LocalNote[] {
  if (q.trim().length < 2 || limit <= 0) return []
  const userId = useAuthStore.getState().user?.id
  if (!userId) return []
  const ids = searchSharedNoteIds(parseSearchQuery(q), new Date(), limit, (sql, params) =>
    query(sql, params).map((row) => Object.values(row)),
  )
  if (ids.length === 0) return []
  const rows = query<SharedStubRow>(`${SHARED_STUB_SELECT} AND snr.note_id IN (${ids.map(() => '?').join(', ')})`, [
    userId,
    ...ids,
  ])
  return rows.map(rowToSharedStub)
}
